import { html } from "lit";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { html as oghtml, utils } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { Command, CommandGroupSource, CommandQuery } from "@omegagrid/commands";
import rp, { CommandProvider } from "@reporting/rp-app-host";
import { DbFunction, DbIndex, DbTrigger, DbProcedure, DbTable, DbView } from "@reporting/rp-types/server";
import { buildIndexScriptId } from "./types";

type DwhSearchResult = {
	tables?: DbTable[];
	views?: DbView[];
	procedures?: DbProcedure[];
	functions?: DbFunction[];
	indexes?: (DbIndex & { parent_table_name?: string; parent_object_id?: number })[];
	triggers?: (DbTrigger & { parent_table_name?: string; parent_object_id?: number })[];
};

export type SearchResultCommand = Command & {
	props: {
		preview?: string;
	};
}

function highlightText(text: string, term: string): string {
	const m = (new RegExp(utils.escapeRegExp(term), 'gi'));
	if (m == null) return null;
	const matches: [number, number][] = [];
	let match: RegExpExecArray;
	while (match = m.exec(text)) {
		matches.push([match.index, match.index + term.length]);
	}
	return oghtml.highlightText(text, matches);
}

export class DWHSearchProvider implements CommandProvider {

	id = 'DWH';
	title = msg('DWH');
	placeholder = msg('Search in data warehouse...');
	triggerCommand = 'd';
	lastQuery: CommandQuery;

	switches = [
		{ key: 'tables', value: msg('Tables'), checked: true },
		{ key: 'views', value: msg('Views'), checked: true },
		{ key: 'procedures', value: msg('Procedures'), checked: true },
		{ key: 'functions', value: msg('Functions'), checked: true },
		{ key: 'indexes', value: msg('Indexes') },
		{ key: 'triggers', value: msg('Triggers') },
	];

	getGroups = async (query?: CommandQuery): Promise<CommandGroupSource[]> => {
		this.lastQuery = query;
		if (!query?.filter) return [];

		const active = query.switches ?? this.switches.filter(s => s.checked).map(s => s.key);
		const result = await rp.ms('server').get<DwhSearchResult>('search', {
			q: query.filter,
			objects: active,
		});

		const groups: CommandGroupSource[] = [];

		if (result.tables?.length) {
			groups.push({
				id: 'tables',
				name: msg('Tables'),
				commands: result.tables.map(t => ({
					id: `dwh-values-table-${t.object_id}`,
					name: t.table_name,
					group: 'tables',
					exec: (c) => { rp.host.open(c.id, { title: c.name }); },
				})),
			});
		}

		if (result.views?.length) {
			groups.push({
				id: 'views',
				name: msg('Views'),
				commands: result.views.map(v => ({
					id: `dwh-values-view-${v.object_id}`,
					name: v.view_name,
					group: 'views',
					exec: (c) => { rp.host.open(c.id, { title: c.name }); },
				})),
			});
		}

		if (result.procedures?.length) {
			groups.push({
				id: 'procedures',
				name: msg('Procedures'),
				commands: result.procedures.map(p => ({
					id: `dwh-sql-procedure-${p.object_id}`,
					name: p.name,
					group: 'procedures',
					exec: (c) => { rp.host.open(c.id, { title: c.name }); },
				})),
			});
		}

		if (result.functions?.length) {
			groups.push({
				id: 'functions',
				name: msg('Functions'),
				commands: result.functions.map(f => ({
					id: `dwh-sql-function-${f.object_id}`,
					name: f.name,
					group: 'functions',
					exec: (c) => { rp.host.open(c.id, { title: c.name }); },
				})),
			});
		}

		if (result.indexes?.length) {
			groups.push({
				id: 'indexes',
				name: msg('Indexes'),
				commands: result.indexes.map(idx => ({
					id: `dwh-sql-index-${buildIndexScriptId(idx.parent_object_id ?? idx.object_id, idx.index_id)}`,
					name: `${idx.parent_table_name ?? idx.table_name} → ${idx.name}`,
					group: 'indexes',
					exec: (c) => { rp.host.open(c.id, { title: c.name }); },
				})),
			});
		}

		if (result.triggers?.length) {
			groups.push({
				id: 'triggers',
				name: msg('Triggers'),
				commands: result.triggers.map(trg => ({
					id: `dwh-sql-trigger-${trg.object_id}`,
					name: `${trg.parent_table_name ?? trg.table_name} → ${trg.name}`,
					group: 'triggers',
					exec: (c) => { rp.host.open(c.id, { title: c.name }); },
				})),
			});
		}

		return groups;
	}

	getOptions = () => ({
		commandRenderer: (div: HTMLDivElement, command: Command) => {
			div.style.height = '22px';
			div.style.lineHeight = '20px';

			const nameHTML = command.name && this.lastQuery?.filter
				? highlightText(command.name, this.lastQuery.filter)
				: command.name;

			return html`
				<div class="command" style="display: flex">
					<og-button
						style="padding-right: 5px; padding-bottom: 4px;"
						color="transparent"
						icon="${command.group === 'procedures' ? 'gears' : command.group === 'functions' ? 'function' : command.group === 'indexes' ? 'key' : command.group === 'triggers' ? 'bolt' : command.group === 'views' ? 'eye' : 'table'}">
					</og-button>
					<div style="flex: 1">${unsafeHTML(nameHTML)}</div>
				</div>
			`;
		}
	});

}
